import {
  AccordionDetails,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { MuiColorInput } from 'mui-color-input';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  AccountData,
  Language,
  PartialLabelData,
  StorefrontButtonShape,
  StorefrontSection,
  StorefrontStylingData,
} from '../../../api';
import { Accordion, AccordionSummary } from '../../../calendar/dialogs/EditOrderDialog/components/Accordion';
import { StorefrontPanels } from '../StorefrontDrawer';
import { useAuth } from '../../../session/InternalAuthProvider';
import { SortableSectionItem } from './SortableSectionItem';
import { useLanguage } from '../../../common/GeneralUtils';

type StylingPanelProps = {
  expanded?: StorefrontPanels;
  onPanelChange: (panel: StorefrontPanels) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
  panelName: StorefrontPanels;
};

type StylingFormValues = {
  styling?: StorefrontStylingData;
  subtitle?: PartialLabelData;
  displaySections: StorefrontSection[];
};

const SHAPE_BORDER_RADIUS: Record<StorefrontButtonShape, number> = {
  [StorefrontButtonShape.SHARP]: 0,
  [StorefrontButtonShape.MEDIUM]: 8,
  [StorefrontButtonShape.ROUNDED]: 20,
};

const SHAPE_TRANSLATION_KEYS: Record<StorefrontButtonShape, string> = {
  [StorefrontButtonShape.SHARP]: 'storefronts.shape_sharp',
  [StorefrontButtonShape.MEDIUM]: 'storefronts.shape_medium',
  [StorefrontButtonShape.ROUNDED]: 'storefronts.shape_rounded',
};

const ALL_SECTIONS: StorefrontSection[] = [
  StorefrontSection.ORDER_PAGES,
  StorefrontSection.TRIALS,
  StorefrontSection.PASS_ORDER_PAGES,
  StorefrontSection.GIFT_CARD,
  StorefrontSection.REVIEWS,
  StorefrontSection.FAQS,
  StorefrontSection.ABOUT,
  StorefrontSection.CUSTOM_INQUIRY,
];

const SECTION_LABEL_KEYS: Record<StorefrontSection, string> = {
  [StorefrontSection.ORDER_PAGES]: 'storefronts.order_pages',
  [StorefrontSection.PASS_ORDER_PAGES]: 'storefronts.pass_order_pages',
  [StorefrontSection.TRIALS]: 'storefronts.trial_order_pages',
  [StorefrontSection.GIFT_CARD]: 'storefronts.gift_card',
  [StorefrontSection.REVIEWS]: 'storefronts.reviews',
  [StorefrontSection.FAQS]: 'storefronts.faqs',
  [StorefrontSection.ABOUT]: 'storefronts.about',
  [StorefrontSection.CUSTOM_INQUIRY]: 'storefronts.custom_inquiry',
};

const getSectionLabel = (section: StorefrontSection, t: (key: string) => string, activityLabel?: string): string => {
  if (section === StorefrontSection.ORDER_PAGES && activityLabel) {
    return activityLabel;
  }
  return t(SECTION_LABEL_KEYS[section]);
};

const DEFAULT_BUTTON_COLOR = '#1976d2';

export const StylingPanel = ({ expanded, onPanelChange, panelName }: StylingPanelProps) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<StylingFormValues>();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const languages = account.customer_facing_widgets_languages;
  const language = useLanguage();
  const activityLabel = account?.activity_label_plural?.[language];

  const { styling, subtitle, displaySections } = values;

  const currentColor = styling?.button_color || DEFAULT_BUTTON_COLOR;
  const currentShape = styling?.button_shape || StorefrontButtonShape.ROUNDED;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleShapeChange = (_: React.MouseEvent<HTMLElement>, newShape: StorefrontButtonShape | null) => {
    if (newShape) {
      void setFieldValue('styling', { ...styling, button_shape: newShape });
    }
  };

  const handleColorChange = (newColor: string) => {
    void setFieldValue('styling', { ...styling, button_color: newColor });
  };

  const handleSubtitleChange = (lang: string, value: string) => {
    void setFieldValue('subtitle', { ...subtitle, [lang]: value });
  };

  const handleSectionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = displaySections.indexOf(active.id as StorefrontSection);
      const newIndex = displaySections.indexOf(over.id as StorefrontSection);
      void setFieldValue('displaySections', arrayMove(displaySections, oldIndex, newIndex));
    }
  };

  const handleSectionToggle = (section: StorefrontSection, wasChecked: boolean) => {
    if (wasChecked) {
      void setFieldValue(
        'displaySections',
        displaySections.filter((s) => s !== section),
      );
    } else {
      void setFieldValue('displaySections', [...displaySections, section]);
    }
  };

  const unselectedSections = ALL_SECTIONS.filter((s) => !displaySections.includes(s));

  return (
    <Accordion expandedPanelName={expanded} panelName={panelName} onChange={onPanelChange}>
      <AccordionSummary title={t('storefronts.design')} />
      <AccordionDetails>
        <Grid container flexDirection="column" gap={3}>
          <Grid container flexDirection="column" gap={1}>
            <Typography fontWeight={600}>{t('storefronts.subtitle')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('storefronts.subtitle_description')}
            </Typography>
            {languages.map((lang) => (
              <TextField
                key={lang}
                label={t('storefronts.subtitle_lang', { lang: t(`emailTemplates.${lang}`) })}
                value={subtitle?.[lang] || ''}
                onChange={(e) => handleSubtitleChange(lang, e.target.value)}
                dir={lang === Language.HE ? 'rtl' : 'ltr'}
                fullWidth
                size="small"
              />
            ))}
          </Grid>

          <Grid container flexDirection="column" gap={1}>
            <Typography fontWeight={600}>{t('storefronts.display_sections')}</Typography>
            <FormGroup>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSectionDragEnd}>
                <SortableContext items={displaySections} strategy={verticalListSortingStrategy}>
                  {displaySections.map((section) => (
                    <SortableSectionItem
                      key={section}
                      id={section}
                      label={getSectionLabel(section, t, activityLabel)}
                      onToggle={handleSectionToggle}
                    />
                  ))}
                </SortableContext>
              </DndContext>
              {unselectedSections.map((section) => (
                <FormControlLabel
                  key={section}
                  control={<Checkbox checked={false} onChange={() => handleSectionToggle(section, false)} />}
                  label={<Typography>{getSectionLabel(section, t, activityLabel)}</Typography>}
                />
              ))}
            </FormGroup>
          </Grid>

          <Grid container flexDirection="column" gap={1}>
            <Typography fontWeight={600}>{t('storefronts.button_shape')}</Typography>
            <ToggleButtonGroup value={currentShape} exclusive onChange={handleShapeChange}>
              {Object.values(StorefrontButtonShape).map((shape) => (
                <ToggleButton key={shape} value={shape} sx={{ p: 1.5 }}>
                  <Box
                    sx={{
                      width: 70,
                      height: 32,
                      border: `2px solid ${currentColor}`,
                      borderRadius: `${SHAPE_BORDER_RADIUS[shape]}px`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: currentColor,
                      fontSize: 12,
                      fontWeight: 500,
                    }}>
                    {t(SHAPE_TRANSLATION_KEYS[shape])}
                  </Box>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Grid>

          <Grid container flexDirection="column" gap={1}>
            <Typography fontWeight={600}>{t('storefronts.button_color')}</Typography>
            <MuiColorInput value={currentColor} onChange={handleColorChange} format="hex" isAlphaHidden />
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
